import {
  useContract,
  useAddress,
  Web3Button,
  ConnectWallet,
} from "@thirdweb-dev/react";
import { useState, useEffect, useCallback } from "react";
import { useToast } from "../components/ui/use-toast";
import { ethers } from "ethers";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import BasedHeader from "../components/BasedHeader";

dayjs.extend(duration);
dayjs.extend(relativeTime);

export default function Claim() {
  const { contract } = useContract("0x22336C29FEc84A3b882699488f798d5a10858B36");
  const address = useAddress();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [availableRewards, setAvailableRewards] = useState<{
    tokenId: string;
    month: number;
    ethAmount: string;
    tokenAmount: string;
  }[]>([]);
  const [nextClaimDate, setNextClaimDate] = useState<Date | null>(null);
  const [timeLeft, setTimeLeft] = useState<string>("");

  useEffect(() => {
    const calculateNextClaimDate = () => {
      const now = Math.floor(Date.now() / 1000); // Current time in seconds
      const currentMonth = Math.floor(now / (30 * 24 * 60 * 60)); // Get the current month number
      const nextClaimTimestamp = (currentMonth + 1) * (30 * 24 * 60 * 60); // Calculate next claim period
      setNextClaimDate(new Date(nextClaimTimestamp * 1000));
    };

    calculateNextClaimDate();
  }, []);

  useEffect(() => {
    if (nextClaimDate) {
      const interval = setInterval(() => {
        const now = dayjs();
        const timeRemaining = dayjs(nextClaimDate).diff(now);
        setTimeLeft(timeRemaining > 0 ? dayjs.duration(timeRemaining).humanize() : "Now");
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [nextClaimDate]);

  const fetchAvailableRewards = useCallback(async () => {
    if (!address || !contract) return;
    try {
      const [months, tokenIds, ethAmounts, tokenAmounts, claimedStatus] =
        await contract.call("getAllRewards", [address]);
      const rewards = [];
      for (let i = 0; i < months.length; i++) {
        if (!claimedStatus[i] && (ethAmounts[i].gt(0) || tokenAmounts[i].gt(0))) {
          rewards.push({
            tokenId: tokenIds[i].toString(),
            month: months[i].toNumber(),
            ethAmount: ethers.utils.formatEther(ethAmounts[i]),
            tokenAmount: ethers.utils.formatUnits(tokenAmounts[i], 18),
          });
        }
      }
      setAvailableRewards(rewards);
    } catch (error) {
      console.error("Error fetching rewards:", error);
      toast({
        title: "Error fetching rewards",
        description: "Failed to fetch available rewards",
        variant: "destructive",
      });
    }
  }, [address, contract, toast]);

  useEffect(() => {
    if (address) {
      setIsLoading(true);
      fetchAvailableRewards().finally(() => setIsLoading(false));
    }
  }, [address, fetchAvailableRewards]);

  const claimAllRewards = useCallback(async (contract: any) => {
    if (!availableRewards.length) {
      toast({
        title: "No unclaimed rewards",
        description: "There are no unclaimed rewards for your tokens.",
      });
      return false;
    }
    const tokenIds = availableRewards.map(r => r.tokenId);
    const months = availableRewards.map(r => r.month);
    await contract.call("batchClaimRewards", [tokenIds, months]);
    return true;
  }, [availableRewards, toast]);

  const totalEthReward = availableRewards
    .reduce((sum, reward) => sum + parseFloat(reward.ethAmount), 0)
    .toFixed(6);

  const totalTokenReward = availableRewards
    .reduce((sum, reward) => sum + parseFloat(reward.tokenAmount), 0)
    .toFixed(2);

  if (!contract) return null;

  return (
    <>
      <BasedHeader />
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white pt-32">
        <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-7xl font-bold text-blue-400 drop-shadow-lg">
  Claim Rewards
</h1>
          <p className="text-xl text-gray-400 mt-4">
            Connect your wallet and claim your rewards instantly! 🚀
          </p>
        </div>

        <div className="container mx-auto px-4 pb-20">
          {!address ? (
            <div className="text-center">
              <p className="text-xl text-gray-400 mb-10">Please connect your wallet.</p>
              <ConnectWallet className="bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-4 px-8 rounded-xl hover:opacity-90 transition" />
            </div>
          ) : isLoading ? (
            <div className="text-center text-gray-400 py-20">⏳ Fetching rewards...</div>
          ) : availableRewards.length === 0 ? (
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4 text-white/90">No Rewards Available</h2>
              <p className="text-gray-400 mb-4">
                You currently have no unclaimed rewards.
              </p>
              {nextClaimDate && (
                <p className="text-lg text-blue-400">
                  ⏳ Next Claim in: <span className="font-bold">{timeLeft}</span>
                </p>
              )}
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-8">
              <div className="glass p-6 rounded-xl border border-blue-500 shadow-lg text-center">
                <h2 className="text-3xl font-bold mb-4 text-blue-400">
                  🎉 You have rewards to claim!
                </h2>
                <p className="text-lg text-gray-300 mb-6">
                  Total $ETH: <span className="text-blue-400">{totalEthReward} ETH</span> | $WEEP: <span className="text-purple-400">{totalTokenReward} Tokens</span>
                </p>
                {nextClaimDate && (
                  <p className="text-lg text-blue-400">
                    ⏳ Next Claim in: <span className="font-bold">{timeLeft}</span>
                  </p>
                )}
              </div>

              <Web3Button
                contractAddress="0x22336C29FEc84A3b882699488f798d5a10858B36"
                action={claimAllRewards}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-3 px-6 rounded-xl hover:opacity-90 transition"
                onSuccess={(result) => {
                  if (result) {
                    toast({
                      title: "Successfully claimed all rewards!",
                      description: "All available rewards have been claimed.",
                    });
                    fetchAvailableRewards();
                  }
                }}
                onError={(error) => {
                  toast({
                    title: "Error claiming rewards",
                    description: error.message,
                    variant: "destructive",
                  });
                }}
              >
                Claim All Rewards
              </Web3Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
